import { useEffect, useState } from "react";
import { Row, Col, Table, Spinner, Form, Button, Modal, Badge, Card } from "react-bootstrap";
import { Link } from 'react-router-dom';
import debounce from 'lodash.debounce';

export default function Proposicoes() {
    const [proposicoes, setProposicoes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [mostrarResultados, setMostrarResultados] = useState(false);
    const [mostrarFiltrosAvancados, setMostrarFiltrosAvancados] = useState(false);
    
    const [formData, setFormData] = useState({
        tipo: '',
        numero: '',
        ano: '',
        legislatura: '',
        filtroAutor: '',
        filtroTexto: '', // Busca na ementa (frontend)
        buscaConteudo: '', // Busca no conteúdo completo (backend)
        dataInicio: '',
        dataFim: ''
    });

    const [dadosFiltrados, setDadosFiltrados] = useState([]);
    const [filtroAtivo, setFiltroAtivo] = useState('todos');
    const [paginaAtual, setPaginaAtual] = useState(1);
    const itensPorPagina = 10;

    // Mapeamento de tipos para códigos tipoprop
    const tipoToTipoProp = {
        'projeto': 'p',
        'indicacao': 'i',
        'parecer': 'pc',
        'requerimento': 'r'
    };
    
    // Função para gerar URL de detalhes (novo sistema ou legado)
    const gerarUrlDetalhes = (item) => {
        // Se temos docid, usar novo sistema
        if (item.dados.docid) {
            const tipoPropCode = tipoToTipoProp[item.tipo] || item.tipo;
            return `/proposicoes/detalhes?docid=${item.dados.docid}&tipoprop=${tipoPropCode}`;
        }
        // Fallback para sistema legado
        return `/proposicoes/detalhes?tipo=${item.tipo}&numero=${item.dados.numero}&ano=${item.dados.ano}`;
    };

    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2000; year--) {
        years.push(year);
    }

    const realizarBusca = async () => {
        setLoading(true);
        setError('');
        setMostrarResultados(false);
        
        try {
            const tiposParaBuscar = formData.tipo ? [formData.tipo] : ['projetos', 'indicacoes', 'requerimentos'];
            let todosOsDados = [];
            
            for (const tipo of tiposParaBuscar) {
                try {
                    const url = construirURL(tipo);
                    const response = await fetch(url);
                    
                    if (!response.ok) {
                        console.warn(`Erro ao buscar ${tipo}: ${response.status}`);
                        continue;
                    }

                    const xmlData = await response.text();
                    const dadosDoTipo = processarXMLParaTipo(xmlData, tipo);
                    todosOsDados = todosOsDados.concat(dadosDoTipo);
                } catch (err) {
                    console.warn(`Erro ao processar ${tipo}:`, err);
                }
            }
            
            // Sort by newest first (date if available, then by year desc, then by number desc within same type)
            todosOsDados.sort((a, b) => {
                // Try to parse dates first
                const dataA = converterDataParaComparacao(a.dados.dataCriacao);
                const dataB = converterDataParaComparacao(b.dados.dataCriacao);
                
                if (dataA && dataB) {
                    return dataB - dataA; // Most recent date first
                }
                
                // Fallback to year, then within same type sort by number
                const anoA = parseInt(a.dados.ano) || 0;
                const anoB = parseInt(b.dados.ano) || 0;
                
                if (anoA !== anoB) {
                    return anoB - anoA; // Year descending
                }
                
                // Only compare numbers within same type
                if (a.tipo === b.tipo) {
                    const numeroA = parseInt(a.dados.numero) || 0;
                    const numeroB = parseInt(b.dados.numero) || 0;
                    return numeroB - numeroA; // Number descending within same type
                }
                
                // Different types, sort by type name
                return a.tipo.localeCompare(b.tipo);
            });
            
            setProposicoes(todosOsDados);
            setDadosFiltrados(todosOsDados);
            aplicarFiltros(todosOsDados);
            setPaginaAtual(1);
            setFiltroAtivo('todos');
            setMostrarResultados(true);
            
        } catch (error) {
            console.error('Erro na busca:', error);
            setError(`Erro ao buscar dados: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const construirURL = (tipo) => {
        let url = `https://dadosabertos.alepe.pe.gov.br/api/v1/proposicoes/${tipo}/`;
        let params = [];

        if (formData.numero && formData.ano) {
            params.push(`numero=${formData.numero}`);
            params.push(`ano=${formData.ano}`);
        } else {
            const anoParaBusca = formData.ano || currentYear;
            params.push(`ano=${anoParaBusca}`);
            
            if (formData.legislatura) {
                params.push(`legislatura=${formData.legislatura}`);
            }
        }
        
        // Adicionar parâmetro de busca por conteúdo completo (backend)
        if (formData.buscaConteudo && formData.buscaConteudo.trim()) {
            params.push(`texto=${encodeURIComponent(formData.buscaConteudo.trim())}`);
        }

        if (params.length > 0) {
            url += '?' + params.join('&');
        }

        return url;
    };

    const processarXMLParaTipo = (xmlData, tipoUrl) => {
        try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xmlData, 'text/xml');
            
            console.log(`Processing ${tipoUrl}, XML length: ${xmlData.length}`);
            console.log(`Root element:`, xmlDoc.documentElement?.tagName);
            
            const errorElement = xmlDoc.querySelector('error message');
            if (errorElement) {
                console.warn(`Erro para tipo ${tipoUrl}: ${errorElement.textContent}`);
                return [];
            }
            
            let dadosDoTipo = [];
            // Convert plural to singular properly
            let tipoSingular;
            if (tipoUrl === 'indicacoes') {
                tipoSingular = 'indicacao';
            } else if (tipoUrl === 'requerimentos') {
                tipoSingular = 'requerimento';
            } else if (tipoUrl === 'projetos') {
                tipoSingular = 'projeto';
            } else {
                // Fallback to old method
                tipoSingular = tipoUrl.slice(0, -1);
            }
            
            console.log(`Looking for elements: ${tipoSingular}`);

            if (formData.numero && formData.ano) {
                const itemElement = xmlDoc.querySelector(tipoSingular);
                if (itemElement) {
                    dadosDoTipo = [{
                        xml: xmlData,
                        tipo: tipoSingular,
                        dados: extrairDadosXML(xmlData)
                    }];
                }
            } else {
                // Multiple propositions - look for individual items within the root
                const itemElements = xmlDoc.querySelectorAll(tipoSingular);
                
                console.log(`Found ${itemElements.length} elements for ${tipoSingular}`);
                console.log(`First few elements:`, Array.from(itemElements).slice(0, 3).map(el => el.outerHTML.substring(0, 200)));
                
                for (let element of itemElements) {
                    const xmlString = new XMLSerializer().serializeToString(element);
                    const dados = extrairDadosXML(xmlString);
                    
                    console.log(`Extracted data for ${dados.numero}/${dados.ano}:`, dados);
                    
                    // Add item regardless of whether some fields are N/A
                    dadosDoTipo.push({
                        xml: xmlString,
                        tipo: tipoSingular,
                        dados: dados
                    });
                }
            }
            
            console.log(`Returning ${dadosDoTipo.length} items for ${tipoUrl}`);
            return dadosDoTipo;
            
        } catch (error) {
            console.error(`Erro ao processar XML para ${tipoUrl}:`, error);
            return [];
        }
    };

    const extrairDadosXML = (xmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, 'text/xml');
        
        const mainElement = doc.querySelector('projeto') || doc.querySelector('indicacao') || doc.querySelector('requerimento');
        
        let dados = {
            numero: 'N/A',
            ano: 'N/A',
            ementa: 'Ementa não disponível',
            situacao: 'N/A',
            autor: 'N/A',
            dataCriacao: 'N/A',
            tipo: 'N/A',
            subtipo: 'N/A',
            legislatura: 'N/A',
            docid: null // Novo campo para suporte ao sistema docid/tipoprop
        };
        
        if (mainElement) {
            // Extract basic attributes
            dados.numero = mainElement.getAttribute('numero') || 'N/A';
            dados.ano = mainElement.getAttribute('ano') || 'N/A';
            dados.tipo = mainElement.getAttribute('tipo') || 'N/A';
            dados.subtipo = mainElement.getAttribute('subtipo') || 'N/A';
            dados.legislatura = mainElement.getAttribute('legislatura') || 'N/A';
            dados.situacao = mainElement.getAttribute('situacao') || 'Tramitando';
            dados.docid = mainElement.getAttribute('docid'); // Extrai o docid do XML
            
            // Extract author
            const autorElement = mainElement.querySelector('autores autor');
            if (autorElement) {
                dados.autor = autorElement.getAttribute('nome') || 'N/A';
            }
            
            // For ementa, use subtipo or tipo as description for now since actual ementa might not be in attributes
            dados.ementa = dados.subtipo && dados.subtipo !== 'N/A' ? dados.subtipo : 
                          (dados.tipo && dados.tipo !== 'N/A' ? dados.tipo : 'Descrição não disponível');
            
            // Extract publication date from attribute
            dados.dataCriacao = mainElement.getAttribute('dataPublicacao') || 'N/A';
        }
        
        return dados;
    };

    const getElementText = (doc, tagName) => {
        const element = doc.querySelector(tagName);
        return element ? element.textContent.trim() : null;
    };

    const converterDataParaComparacao = (dataStr) => {
        if (!dataStr || dataStr === 'N/A') return null;
        
        // Try different date formats
        const formatos = [
            /^(\d{4})-(\d{2})-(\d{2})/, // YYYY-MM-DD
            /^(\d{2})\/(\d{2})\/(\d{4})/, // DD/MM/YYYY
            /^(\d{4})\/(\d{2})\/(\d{2})/ // YYYY/MM/DD
        ];
        
        for (let formato of formatos) {
            const match = dataStr.match(formato);
            if (match) {
                let ano, mes, dia;
                if (formato.source.startsWith('^(\\d{4})')) {
                    ano = parseInt(match[1]);
                    mes = parseInt(match[2]) - 1; // JS months are 0-based
                    dia = parseInt(match[3]);
                } else {
                    dia = parseInt(match[1]);
                    mes = parseInt(match[2]) - 1;
                    ano = parseInt(match[3]);
                }
                return new Date(ano, mes, dia);
            }
        }
        
        return null;
    };

    const aplicarFiltros = (dados = dadosFiltrados) => {
        let dadosFiltrados = [...dados];
        
        if (formData.filtroAutor) {
            dadosFiltrados = dadosFiltrados.filter(item => 
                item.dados.autor && 
                item.dados.autor.toLowerCase().includes(formData.filtroAutor.toLowerCase())
            );
        }
        
        // Filtro por ementa (processado no frontend)
        if (formData.filtroTexto) {
            dadosFiltrados = dadosFiltrados.filter(item => 
                item.dados.ementa && 
                item.dados.ementa.toLowerCase().includes(formData.filtroTexto.toLowerCase())
            );
        }
        
        setDadosFiltrados(dadosFiltrados);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const debouncedAplicarFiltros = debounce(() => {
        aplicarFiltros();
    }, 500);

    const handleFilterInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        debouncedAplicarFiltros();
    };

    const limparFormulario = () => {
        setFormData({
            tipo: '',
            numero: '',
            ano: '',
            legislatura: '',
            filtroAutor: '',
            filtroTexto: '',
            buscaConteudo: '',
            dataInicio: '',
            dataFim: ''
        });
        setMostrarFiltrosAvancados(false);
        setMostrarResultados(false);
        setError('');
        setProposicoes([]);
        setDadosFiltrados([]);
        setPaginaAtual(1);
        setFiltroAtivo('todos');
    };

    const filtrarTodos = () => {
        setDadosFiltrados([...proposicoes]);
        setPaginaAtual(1);
        setFiltroAtivo('todos');
    };

    const filtrarPorTipo = (tipo) => {
        const dadosFiltrados = proposicoes.filter(item => item.tipo === tipo);
        setDadosFiltrados(dadosFiltrados);
        setPaginaAtual(1);
        setFiltroAtivo(tipo);
    };

    const contarTipo = (tipo) => {
        return proposicoes.filter(item => item.tipo === tipo).length;
    };

    const getTipoNome = (tipo) => {
        const nomes = {
            'projeto': 'Projeto',
            'indicacao': 'Indicação',
            'requerimento': 'Requerimento'
        };
        return nomes[tipo] || tipo.charAt(0).toUpperCase() + tipo.slice(1);
    };

    const getTipoBadgeClass = (tipo) => {
        const classes = {
            'projeto': 'success',
            'indicacao': 'info',
            'requerimento': 'warning'
        };
        return classes[tipo] || 'secondary';
    };

    const formatarData = (data) => {
        if (!data || data === 'N/A') return 'N/A';
        
        const formatos = [
            /^(\d{4})-(\d{2})-(\d{2})/,
            /^(\d{2})\/(\d{2})\/(\d{4})/,
            /^(\d{4})\/(\d{2})\/(\d{2})/
        ];
        
        for (let formato of formatos) {
            const match = data.match(formato);
            if (match) {
                let dia, mes, ano;
                if (formato.source.startsWith('^(\\d{4})')) {
                    ano = match[1];
                    mes = match[2];
                    dia = match[3];
                } else {
                    dia = match[1];
                    mes = match[2];
                    ano = match[3];
                }
                return `${dia}/${mes}/${ano}`;
            }
        }
        
        return data;
    };

    const truncateText = (text, maxLength) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    // Pagination
    const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina);
    const dadosPagina = dadosFiltrados.slice((paginaAtual - 1) * itensPorPagina, paginaAtual * itensPorPagina);

    const trocarPagina = (direction) => {
        if (direction === -1 && paginaAtual > 1) {
            setPaginaAtual(paginaAtual - 1);
        } else if (direction === 1 && paginaAtual < totalPaginas) {
            setPaginaAtual(paginaAtual + 1);
        }
    };

    useEffect(() => {
        // Auto-search for all types from current year when page loads
        setTimeout(() => realizarBusca(), 100);
    }, []);

    return (
        <Row>
            <Col xs="12">
                <p><small>Consulta Interativa de Proposições da ALEPE</small></p>
            </Col>
            
            {/* Search Form */}
            <Col xs="12">
                <Card className="mb-4">
                    <Card.Body>
                        <h5>Filtros de Busca</h5>
                        <Form onSubmit={(e) => { e.preventDefault(); realizarBusca(); }}>
                            <Row className="g-3">
                                <Col md={4}>
                                    <Form.Group controlId="tipo">
                                        <Form.Label>Tipo de Proposição</Form.Label>
                                        <Form.Select
                                            name="tipo"
                                            value={formData.tipo}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Todos os tipos</option>
                                            <option value="projetos">📋 Projetos de Lei</option>
                                            <option value="indicacoes">📝 Indicações</option>
                                            <option value="requerimentos">📄 Requerimentos</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                
                                <Col md={2}>
                                    <Form.Group controlId="numero">
                                        <Form.Label>Número</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="numero"
                                            value={formData.numero}
                                            onChange={handleInputChange}
                                            placeholder="Ex: 123"
                                        />
                                    </Form.Group>
                                </Col>
                                
                                <Col md={2}>
                                    <Form.Group controlId="ano">
                                        <Form.Label>Ano</Form.Label>
                                        <Form.Select
                                            name="ano"
                                            value={formData.ano}
                                            onChange={handleInputChange}
                                        >
                                            <option value="">Selecione</option>
                                            {years.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                
                                <Col md={2}>
                                    <Form.Group controlId="legislatura">
                                        <Form.Label>Legislatura</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="legislatura"
                                            value={formData.legislatura}
                                            onChange={handleInputChange}
                                            placeholder="Ex: 19"
                                        />
                                    </Form.Group>
                                </Col>
                                
                                <Col md={2} className="d-flex align-items-end">
                                    <Button 
                                        variant="primary" 
                                        onClick={realizarBusca} 
                                        disabled={loading}
                                        className="w-100"
                                    >
                                        {loading ? 'Buscando...' : 'Buscar'}
                                    </Button>
                                </Col>
                            </Row>
                            
                            {/* Advanced Filters */}
                            <Row className="mt-3">
                                <Col xs="12">
                                    <Button 
                                        variant="outline-secondary" 
                                        size="sm"
                                        onClick={() => setMostrarFiltrosAvancados(!mostrarFiltrosAvancados)}
                                    >
                                        Filtros Avançados {mostrarFiltrosAvancados ? '▲' : '▼'}
                                    </Button>
                                </Col>
                            </Row>
                            
                            {mostrarFiltrosAvancados && (
                                <Row className="mt-3 p-3 bg-light rounded">
                                    <Col md={4}>
                                        <Form.Group controlId="filtroAutor">
                                            <Form.Label>Autor</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="filtroAutor"
                                                value={formData.filtroAutor}
                                                onChange={handleFilterInputChange}
                                                placeholder="Nome do autor..."
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group controlId="filtroTexto">
                                            <Form.Label>Buscar na Ementa</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="filtroTexto"
                                                value={formData.filtroTexto}
                                                onChange={handleFilterInputChange}
                                                placeholder="Buscar na ementa..."
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4} className="d-flex align-items-end">
                                        <Button 
                                            variant="outline-warning" 
                                            size="sm"
                                            onClick={limparFormulario}
                                        >
                                            Limpar Tudo
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                            
                            {/* Busca de Conteúdo Completo - Seção Separada */}
                            <Row className="mt-4">
                                <Col xs="12">
                                    <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6', borderWidth: '2px' }}>
                                        <h6 className="mb-3" style={{ color: '#495057', fontWeight: '600' }}>
                                            <i className="bi bi-search"></i> Busca no Conteúdo Completo da Proposição
                                            <small className="text-muted d-block" style={{ fontSize: '0.85em', fontWeight: 'normal' }}>
                                                Busca em ementa, matéria, justificativa e demais textos (processamento no servidor)
                                            </small>
                                        </h6>
                                        <Row>
                                            <Col md={8}>
                                                <Form.Group controlId="buscaConteudo">
                                                    <Form.Control
                                                        type="text"
                                                        name="buscaConteudo"
                                                        value={formData.buscaConteudo}
                                                        onChange={handleFilterInputChange}
                                                        placeholder="Digite o texto para buscar em todo o conteúdo da proposição..."
                                                        style={{ fontSize: '1.1em', padding: '10px' }}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4} className="d-flex align-items-end">
                                                <Button 
                                                    variant="primary"
                                                    onClick={realizarBusca}
                                                    disabled={!formData.buscaConteudo?.trim() || loading}
                                                    style={{ padding: '10px 20px', fontWeight: '600' }}
                                                >
                                                    {loading ? (
                                                        <>
                                                            <Spinner
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                                className="me-2"
                                                            />
                                                            Buscando...
                                                        </>
                                                    ) : (
                                                        'Buscar no Conteúdo'
                                                    )}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>
            </Col>

            {/* Loading */}
            {loading && (
                <Col xs="12" className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Buscando proposições...</span>
                    </Spinner>
                    <div className="mt-3">
                        <p className="text-muted">Buscando proposições...</p>
                    </div>
                </Col>
            )}

            {/* Results */}
            {mostrarResultados && !loading && (
                <>
                    {/* Quick Filters */}
                    {dadosFiltrados.length > 0 && (
                        <Col xs="12" className="mb-3">
                            <Card>
                                <Card.Body>
                                    <h6>Filtros Rápidos</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Button
                                            variant={filtroAtivo === 'todos' ? 'primary' : 'outline-primary'}
                                            size="sm"
                                            onClick={filtrarTodos}
                                        >
                                            Todos ({proposicoes.length})
                                        </Button>
                                        {contarTipo('projeto') > 0 && (
                                            <Button
                                                variant={filtroAtivo === 'projeto' ? 'success' : 'outline-success'}
                                                size="sm"
                                                onClick={() => filtrarPorTipo('projeto')}
                                            >
                                                Projetos ({contarTipo('projeto')})
                                            </Button>
                                        )}
                                        {contarTipo('indicacao') > 0 && (
                                            <Button
                                                variant={filtroAtivo === 'indicacao' ? 'info' : 'outline-info'}
                                                size="sm"
                                                onClick={() => filtrarPorTipo('indicacao')}
                                            >
                                                Indicações ({contarTipo('indicacao')})
                                            </Button>
                                        )}
                                        {contarTipo('requerimento') > 0 && (
                                            <Button
                                                variant={filtroAtivo === 'requerimento' ? 'warning' : 'outline-warning'}
                                                size="sm"
                                                onClick={() => filtrarPorTipo('requerimento')}
                                            >
                                                Requerimentos ({contarTipo('requerimento')})
                                            </Button>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    )}


                    {/* Results Table */}
                    {dadosFiltrados.length > 0 && (
                        <Col xs="12">
                            <div className="table-responsive">
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Tipo</th>
                                            <th>Número/Ano</th>
                                            <th>Autor</th>
                                            <th>Ementa/Descrição</th>
                                            <th>Situação</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dadosPagina.map((item, index) => (
                                            <tr key={index}>
                                                <td style={{ width: '10%' }}>
                                                    <Badge bg={getTipoBadgeClass(item.tipo)}>
                                                        {getTipoNome(item.tipo)}
                                                    </Badge>
                                                </td>
                                                <td style={{ width: '12%' }}>
                                                    <strong>{item.dados.numero}/{item.dados.ano}</strong>
                                                    {item.dados.dataCriacao !== 'N/A' && (
                                                        <div>
                                                            <small className="text-muted">
                                                                {formatarData(item.dados.dataCriacao)}
                                                            </small>
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ width: '15%' }}>
                                                    {item.dados.autor !== 'N/A' ? item.dados.autor : '-'}
                                                </td>
                                                <td style={{ width: '40%' }}>
                                                    <div>
                                                        {truncateText(item.dados.ementa, 200)}
                                                    </div>
                                                    {item.dados.subtipo && item.dados.subtipo !== 'N/A' && item.dados.subtipo.trim() !== '' && (
                                                        <div className="mt-1">
                                                            <small className="text-muted">
                                                                <em>Subtipo: {item.dados.subtipo}</em>
                                                            </small>
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ width: '13%' }}>
                                                    {item.dados.situacao !== 'N/A' ? (
                                                        <Badge bg="info" text="white">
                                                            {truncateText(item.dados.situacao, 25)}
                                                        </Badge>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td style={{ width: '10%' }}>
                                                    <Link
                                                        to={gerarUrlDetalhes(item)}
                                                        className="btn btn-primary btn-sm"
                                                    >
                                                        Ver Detalhes
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    )}

                    {/* No results */}
                    {dadosFiltrados.length === 0 && (
                        <Col xs="12" className="text-center py-5">
                            <p className="text-muted">Nenhum resultado encontrado.</p>
                        </Col>
                    )}

                    {/* Pagination */}
                    {totalPaginas > 1 && (
                        <>
                            <Col xs="6" className="text-start">
                                {paginaAtual > 1 ? 
                                    <a href="#" onClick={(e) => { e.preventDefault(); trocarPagina(-1); }}>Página Anterior</a>
                                : null}
                            </Col> 
                            <Col xs="6" className="text-end">
                                {paginaAtual < totalPaginas ? 
                                    <a href="#" onClick={(e) => { e.preventDefault(); trocarPagina(1); }}>Próxima Página</a>
                                : null}
                            </Col>
                        </>
                    )}
                </>
            )}

            {/* Error Message */}
            {error && (
                <Col xs="12">
                    <div className="alert alert-danger">
                        <strong>Erro:</strong> {error}
                    </div>
                </Col>
            )}
        </Row>
    );
}